import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ShieldCheck, TrendingUp, Coins, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { publicApi } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import JanteLogo from "@/components/JanteLogo";

const HERO_IMG =
  "https://images.pexels.com/photos/13722886/pexels-photo-13722886.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const PRINCIPLES = [
  {
    icon: Coins,
    title: "Invest from $1,000",
    body: "A quietly low barrier to entry. Own a share of institutional-style commercial real estate from one thousand dollars.",
  },
  {
    icon: TrendingUp,
    title: "Income & appreciation",
    body: "Earn from operating income while your share accrues alongside long-term property appreciation.",
  },
  {
    icon: ShieldCheck,
    title: "Considered, vetted assets",
    body: "Every property is curated by our Janté Lux Hotels team — boutique stays, hospitality assets and resort homes.",
  },
];

const fadeUp = {
  initial: { opacity: 0, y: 16 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.3 },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
};

const AMOUNT_PRESETS = [1000, 2500, 5000, 10000];

export default function Investing() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [amount, setAmount] = useState(1000);
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !phone.trim() || !message.trim() || !amount) {
      toast.error("Please complete every field to continue.");
      return;
    }
    setSubmitting(true);
    try {
      await publicApi.post("/investing/leads", {
        full_name: name.trim(),
        email: email.trim(),
        phone: phone.trim(),
        amount_usd: Number(amount),
        message: message.trim(),
      });
      setSuccess(true);
      toast.success("Thank you — our team will reach out shortly.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not submit. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main data-testid="investing-page" className="bg-[#FAF9F6]">
      {/* HERO */}
      <section className="relative">
        <div className="max-w-7xl mx-auto px-6 md:px-12 pt-16 lg:pt-24 pb-16 lg:pb-24 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-7"
          >
            <div className="flex items-center gap-3 mb-5">
              <JanteLogo size={22} />
              <span className="eyebrow" data-testid="investing-eyebrow">
                A Janté Lux investment program
              </span>
            </div>
            <h1
              data-testid="investing-headline"
              className="font-serif-display text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-[#2A1F1A]"
            >
              Investing is
              <br />
              <span className="italic text-[#C03028]">not out of reach.</span>
            </h1>
            <p className="mt-7 text-base lg:text-lg leading-relaxed text-[#6E6E70] max-w-xl">
              Invest just <strong className="text-[#2A1F1A]">$1,000</strong> and
              own part of commercial real estate. A quiet, considered way to
              participate in the hospitality assets we host through Sasha.
            </p>

            <div className="mt-9 flex flex-col sm:flex-row gap-4">
              <a
                href="#invest-form"
                data-testid="investing-cta-primary"
                className="btn-primary inline-flex items-center justify-center gap-2 px-7 py-3.5 text-sm tracking-wide rounded-none"
              >
                Reserve your interest
                <ArrowRight size={16} />
              </a>
              <a
                href="https://AssetsRealEstateConsulting.com"
                target="_blank"
                rel="noreferrer"
                data-testid="investing-external-link"
                className="btn-secondary inline-flex items-center justify-center gap-2 px-7 py-3.5 text-sm tracking-wide rounded-none"
              >
                AssetsRealEstateConsulting.com
              </a>
            </div>

            <div className="mt-14 grid grid-cols-3 gap-6 max-w-md">
              {[
                ["$1k", "minimum"],
                ["7.8%", "avg. yield"],
                ["12+", "active assets"],
              ].map(([k, v]) => (
                <div key={k}>
                  <div className="font-serif-display text-3xl text-[#2A1F1A]">{k}</div>
                  <div className="text-xs uppercase tracking-[0.18em] text-[#8E8F90] mt-1">{v}</div>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-5 relative"
          >
            <div className="relative grain-overlay overflow-hidden">
              <img
                src={HERO_IMG}
                alt="A considered, premium hospitality space"
                className="w-full h-[520px] lg:h-[620px] object-cover"
              />
            </div>
            <div className="absolute -bottom-6 -left-6 hidden md:block bg-[#FAF9F6] border border-[#E3DED5] px-6 py-4 max-w-xs">
              <div className="eyebrow">Asset No. 014</div>
              <div className="font-serif-display text-lg text-[#2A1F1A] mt-1">
                Janté Lux · Boutique Hotel, Lisbon
              </div>
              <div className="text-xs text-[#6E6E70] mt-1">
                Funded by 412 investors · 92% occupancy
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* PRINCIPLES */}
      <section className="bg-[#F2EFE9] border-y border-[#E3DED5]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 lg:py-24 grid grid-cols-1 md:grid-cols-3 gap-10">
          {PRINCIPLES.map((p, idx) => {
            const Icon = p.icon;
            return (
              <motion.div
                key={p.title}
                {...fadeUp}
                transition={{ ...fadeUp.transition, delay: idx * 0.1 }}
                data-testid={`investing-principle-${idx}`}
              >
                <div className="w-12 h-12 border border-[#C03028]/30 flex items-center justify-center">
                  <Icon className="text-[#C03028]" size={20} strokeWidth={1.6} />
                </div>
                <div className="eyebrow mt-5">{`Principle 0${idx + 1}`}</div>
                <h3 className="font-serif-display text-2xl mt-1 text-[#2A1F1A]">
                  {p.title}
                </h3>
                <p className="mt-3 text-[#6E6E70] leading-relaxed">{p.body}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="bg-[#FAF9F6]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 lg:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <motion.div {...fadeUp} className="lg:col-span-5">
            <div className="eyebrow">How it works</div>
            <h2 className="font-serif-display mt-5 text-4xl lg:text-5xl text-[#2A1F1A] leading-tight">
              Three quiet steps to ownership.
            </h2>
            <p className="mt-5 text-[#6E6E70] leading-relaxed max-w-md">
              A simple, transparent way to participate in the hospitality
              assets curated by Janté Lux Hotels.
            </p>
          </motion.div>

          <div className="lg:col-span-7 space-y-10">
            {[
              {
                k: "Reserve your interest",
                v: "Tell us a little about yourself and the amount you&apos;d like to invest. We&apos;ll send you a curated list of available assets.",
              },
              {
                k: "Choose your share",
                v: "Pick the property and the size of your participation — from $1,000 upward. Sign your subscription digitally.",
              },
              {
                k: "Receive distributions",
                v: "Quarterly distributions from operating income, with annual statements on appreciation and portfolio performance.",
              },
            ].map((s, i) => (
              <motion.div
                key={s.k}
                {...fadeUp}
                transition={{ ...fadeUp.transition, delay: i * 0.1 }}
                data-testid={`investing-step-${i}`}
                className="flex gap-6 items-start border-t border-[#E3DED5] pt-8 first:border-t-0 first:pt-0"
              >
                <div className="font-serif-display text-4xl text-[#C03028] leading-none">
                  {`0${i + 1}`}
                </div>
                <div>
                  <h3 className="font-serif-display text-2xl text-[#2A1F1A]">
                    {s.k}
                  </h3>
                  <p
                    className="mt-2 text-[#6E6E70] leading-relaxed max-w-lg"
                    dangerouslySetInnerHTML={{ __html: s.v }}
                  />
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FORM */}
      <section id="invest-form" className="bg-[#2A1F1A] text-[#FAF9F6]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <motion.div {...fadeUp} className="lg:col-span-5">
            <div className="eyebrow" style={{ color: "#B0895C" }}>
              Reserve your interest
            </div>
            <h2 className="font-serif-display mt-5 text-4xl lg:text-5xl leading-tight">
              Begin from $1,000.
            </h2>
            <p className="mt-6 text-[#FAF9F6]/70 leading-relaxed max-w-md">
              Share a few details and our team at AssetsRealEstateConsulting
              will reach out with curated opportunities.
            </p>

            <div className="mt-10 flex items-center gap-4">
              <JanteLogo size={40} />
              <div>
                <div className="font-serif-display italic text-2xl text-[#FAF9F6]">
                  Janté Lux
                </div>
                <div className="text-[10px] uppercase tracking-[0.32em] text-[#8E8F90]">
                  Hotels
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div {...fadeUp} className="lg:col-span-7">
            {success ? (
              <div
                data-testid="investing-success"
                className="bg-[#FAF9F6] text-[#2A1F1A] p-8 lg:p-10 border-l-2 border-[#C03028]"
              >
                <div className="eyebrow">Received</div>
                <h3 className="font-serif-display text-3xl mt-3">
                  Thank you, {name.split(" ")[0]}.
                </h3>
                <p className="mt-3 text-[#6E6E70] leading-relaxed">
                  Your interest is now with our investment team at
                  AssetsRealEstateConsulting. You&apos;ll hear from us within two
                  working days with curated opportunities.
                </p>
                <div className="mt-6 flex gap-4">
                  <Link
                    to="/"
                    className="btn-secondary inline-flex items-center gap-2 px-5 py-2.5 text-sm rounded-none"
                  >
                    Return home
                  </Link>
                  <button
                    onClick={() => {
                      setSuccess(false);
                      setName("");
                      setEmail("");
                      setPhone("");
                      setMessage("");
                    }}
                    className="btn-primary inline-flex items-center gap-2 px-5 py-2.5 text-sm rounded-none"
                  >
                    Submit another interest
                  </button>
                </div>
              </div>
            ) : (
              <form
                onSubmit={submit}
                data-testid="investing-form"
                className="bg-[#FAF9F6] text-[#2A1F1A] p-8 lg:p-10 space-y-6"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <Label className="eyebrow">Full name *</Label>
                    <Input
                      data-testid="investing-name"
                      required
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="editorial-input mt-2"
                    />
                  </div>
                  <div>
                    <Label className="eyebrow">Email *</Label>
                    <Input
                      data-testid="investing-email"
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="editorial-input mt-2"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <Label className="eyebrow">Phone *</Label>
                    <Input
                      data-testid="investing-phone"
                      required
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      className="editorial-input mt-2"
                    />
                  </div>
                </div>

                <div>
                  <Label className="eyebrow">Indicative amount (USD) *</Label>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {AMOUNT_PRESETS.map((a) => (
                      <button
                        type="button"
                        key={a}
                        onClick={() => setAmount(a)}
                        data-testid={`investing-amount-${a}`}
                        className={`px-4 py-2 text-sm border transition-colors ${
                          amount === a
                            ? "bg-[#C03028] text-white border-[#C03028]"
                            : "bg-transparent text-[#2A1F1A] border-[#E3DED5] hover:border-[#C03028]"
                        }`}
                      >
                        ${a.toLocaleString()}
                      </button>
                    ))}
                  </div>
                  <Input
                    data-testid="investing-amount-custom"
                    type="number"
                    min={1000}
                    step={500}
                    required
                    value={amount}
                    onChange={(e) => setAmount(Number(e.target.value))}
                    className="editorial-input mt-3"
                    placeholder="Custom amount (USD)"
                  />
                </div>

                <div>
                  <Label className="eyebrow">What interests you most? *</Label>
                  <Textarea
                    data-testid="investing-message"
                    required
                    rows={4}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="A few words on what brings you here."
                    className="mt-2 bg-transparent border border-[#E3DED5] focus-visible:ring-0 focus-visible:border-[#C03028] rounded-none shadow-none p-3 font-sans-body"
                  />
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="investing-submit"
                  className="btn-primary w-full inline-flex items-center justify-center gap-2 py-3.5 text-sm tracking-wide rounded-none"
                >
                  {submitting ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    <ArrowRight size={14} />
                  )}
                  {submitting ? "Sending…" : "Reserve my interest"}
                </button>
              </form>
            )}
          </motion.div>
        </div>
      </section>

      {/* Apply-as-host CTA */}
      <section className="bg-[#FAF9F6]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 py-20 text-center">
          <div className="eyebrow">Already a host?</div>
          <h2 className="font-serif-display mt-5 text-3xl lg:text-4xl text-[#2A1F1A]">
            Host with Sasha while you invest with Janté Lux.
          </h2>
          <p className="mt-4 text-[#6E6E70] max-w-xl mx-auto">
            Two quiet sides of the same considered approach to hospitality.
          </p>
          <Link
            to="/onboard"
            data-testid="investing-host-cta"
            className="btn-primary mt-8 inline-flex items-center gap-2 px-7 py-3.5 text-sm tracking-wide rounded-none"
          >
            Start your host application
            <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </main>
  );
}
